import React from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import {
  MessageSquare, ShieldCheck, Lock, Users, Fingerprint, Sparkles, Bell,
  ScanLine, KeyRound, Cloud, HeartHandshake, Github, Globe, ChevronRight,
} from "lucide-react";

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`about-section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display font-semibold text-sm">{title}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
        </div>
      </div>
      <div className="mt-3 text-[13px] text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

export default function About() {
  const nav = useNavigate();

  return (
    <>
      <TopBar title="About Impo" subtitle="One app. Everything important." back showHome={false} />
      <div className="px-4 py-4 space-y-4" data-testid="about-page">
        {/* Hero */}
        <div className="rounded-3xl border border-border overflow-hidden relative" data-testid="about-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/15 to-primary/5" aria-hidden />
          <div className="relative p-6">
            <div className="flex items-center gap-2">
              <Logo className="h-8" />
              <Badge className="rounded-full text-[10px]">v1.2</Badge>
            </div>
            <div className="font-display font-bold text-2xl mt-3 leading-tight">
              Your AI-powered <br />personal memory OS.
            </div>
            <div className="text-[13px] text-muted-foreground mt-2 max-w-xs">
              Impo is a chat-first, secure vault for the memories that matter — notes, passwords, documents, photos, reminders, and journals — all searchable, all yours.
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button size="sm" onClick={() => nav("/app/chat")} data-testid="about-cta-chat">
                <MessageSquare className="h-4 w-4 mr-1.5" /> Try Impo AI
              </Button>
              <Button size="sm" variant="outline" onClick={() => nav("/app/plan")} data-testid="about-cta-plan">
                Plans & pricing
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </div>

        {/* Positioning */}
        <Section icon={Sparkles} title="What Impo does" subtitle="Say it. Save it. Find it.">
          Impo turns natural conversation into structured memory. Ask it to save a note, set a reminder, or find a receipt — and it does. Everything is categorized, searchable, and pinned wherever you need it.
        </Section>

        {/* Feature grid */}
        <div className="grid grid-cols-2 gap-3">
          <FeatureCard icon={MessageSquare} title="AI Chat"     desc="Claude Sonnet 4.5 routes your intent — no menus." />
          <FeatureCard icon={ScanLine}      title="Scan to PDF" desc="One tap, multi-page, OCR-searchable." />
          <FeatureCard icon={KeyRound}      title="Password Vault" desc="Fernet-encrypted, passkey-locked, history-tracked." />
          <FeatureCard icon={Bell}          title="Reminders"    desc="Custom alarms + web push, even when Impo is closed." />
          <FeatureCard icon={Cloud}         title="Everything, safe" desc="Photos, videos, docs — categorized automatically." />
          <FeatureCard icon={HeartHandshake} title="Digital Legacy" desc="Nominee vaults with owner veto & activity heartbeat." />
        </div>

        {/* Security narrative */}
        <Section icon={ShieldCheck} title="Security by design" subtitle="Built for what matters most">
          <ul className="space-y-2 text-[13px]">
            <li className="flex gap-2"><Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>Passwords are Fernet-encrypted server-side; keys never leave our HSM boundary.</span></li>
            <li className="flex gap-2"><Fingerprint className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>WebAuthn passkeys unlock the vault with your device biometrics.</span></li>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>Vault auto-locks after 5 minutes of inactivity.</span></li>
            <li className="flex gap-2"><Users className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>Household sharing is per-item and revocable.</span></li>
          </ul>
        </Section>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1">The team behind Impo</div>
          <div className="font-display font-semibold text-sm">Built with obsession for the little things.</div>
          <div className="text-[13px] text-muted-foreground mt-1">Designed in Mumbai · Powered by Claude Sonnet 4.5 · Payments by Razorpay · Push via VAPID.</div>
          <div className="flex items-center gap-3 mt-3">
            <a
              href="https://impoapp.com"
              target="_blank"
              rel="noreferrer"
              className="text-[12px] text-primary inline-flex items-center gap-1 hover:underline"
              data-testid="about-link-website"
            >
              <Globe className="h-3.5 w-3.5" /> impoapp.com
            </a>
          </div>
        </div>

        <div className="text-[11px] text-muted-foreground text-center px-2 py-2">
          Impo v1.2 · © {new Date().getFullYear()} · Made with care.
        </div>
      </div>
    </>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3" data-testid={`about-feature-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-display font-semibold text-[13px] mt-2">{title}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</div>
    </div>
  );
}
